import React from "react";
import { Link } from "react-router-dom";
import { ChevronRight } from "lucide-react";

export interface Crumb {
  label: string;
  href?: string;
  onClick?: (event: React.MouseEvent) => void;
}

/**
 * Breadcrumb trail rendered in the portal's top header (dark bar). The last
 * entry is the current page. Single-crumb trails render nothing — only show
 * a trail once you've drilled in (design-system-spec §2.7).
 *
 * The admin portal derives its trail from the route (`deriveCrumbs`); the
 * client portal publishes explicit trails via the shell's `setBreadcrumbs`
 * outlet-context callback. Both feed this one renderer (§4.8).
 *
 * `showSingle` opts out of the hide-single-crumb rule for sections that use the
 * trail as their only page title (e.g. the client Settings section).
 */
export function Breadcrumbs({ items, showSingle = false }: { items?: Crumb[]; showSingle?: boolean }) {
  if (!items || items.length < (showSingle ? 1 : 2)) return null;

  return (
    <nav aria-label="Breadcrumb" className="flex items-center gap-1.5 text-sm">
      {items.map((crumb, i) => {
        const isLast = i === items.length - 1;
        return (
          <React.Fragment key={i}>
            {i > 0 && (
              <ChevronRight className="h-3.5 w-3.5 shrink-0 text-[var(--color-header-foreground)]/40" />
            )}
            {crumb.href && !isLast ? (
              <Link
                to={crumb.href}
                onClick={crumb.onClick}
                className="text-[var(--color-header-foreground)]/70 transition-colors hover:text-[var(--color-header-foreground)]"
              >
                {crumb.label}
              </Link>
            ) : (
              <span
                aria-current={isLast ? "page" : undefined}
                className={
                  isLast
                    ? "font-medium text-[var(--color-header-foreground)]"
                    : "text-[var(--color-header-foreground)]/70"
                }
              >
                {crumb.label}
              </span>
            )}
          </React.Fragment>
        );
      })}
    </nav>
  );
}
